import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { Btn, Loader, EmptyState, Field, TextInput, TextArea, SelectInput } from "../components/Primitives";
import { useAuth } from "../lib/auth";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";

const RISK_COLORS = {
  low: { bg: "var(--success-soft)", color: "var(--success)" },
  medium: { bg: "var(--warn-soft)", color: "var(--warn)" },
  high: { bg: "var(--danger-soft)", color: "var(--danger)" },
  critical: { bg: "var(--danger-soft)", color: "var(--danger)" },
};
const STATUS_COLORS = {
  draft: { bg: "var(--bg-2)", color: "var(--muted)" },
  under_review: { bg: "var(--warn-soft)", color: "var(--warn)" },
  approved: { bg: "var(--success-soft)", color: "var(--success)" },
  rejected: { bg: "var(--danger-soft)", color: "var(--danger)" },
};

function Pill({ label, mapObj }) {
  const t = mapObj?.[label] || { bg: "var(--bg-2)", color: "var(--muted)" };
  return (
    <span className="inline-flex items-center gap-1 px-2 py-[3px] text-[10.5px] font-medium rounded-md"
      style={{ background: t.bg, color: t.color }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: t.color }} />
      {label?.replace(/_/g, " ")}
    </span>
  );
}

function StatCard({ label, value, tone = "primary", testId, hint }) {
  const c = tone === "danger" ? "var(--danger)" : tone === "success" ? "var(--success)"
          : tone === "warn" ? "var(--warn)" : "var(--primary)";
  return (
    <div className="surface rounded-xl p-4" style={{ border: "1px solid var(--border)" }}>
      <div className="text-[11px] font-semibold uppercase tracking-wider muted">{label}</div>
      <div data-testid={testId} className="text-2xl font-semibold mt-1" style={{ color: c }}>{value}</div>
      {hint && <div className="text-[11px] muted mt-1">{hint}</div>}
    </div>
  );
}

export default function DPIA() {
  const { user } = useAuth();
  const canSignoff = ["super_admin", "org_admin", "dpo", "legal_reviewer", "security_officer"].includes(user?.role);
  const [tab, setTab] = useState("dashboard");
  const [dash, setDash] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);

  const [screenModal, setScreenModal] = useState(false);
  const [screenAns, setScreenAns] = useState({});
  const [screenName, setScreenName] = useState("");
  const [screenResult, setScreenResult] = useState(null);

  const [caseModal, setCaseModal] = useState(false);
  const [caseForm, setCaseForm] = useState({
    activity_name: "", scale_of_processing: "medium", answers: {},
    risks_to_principals: "", necessity_assessment: "", proportionality_assessment: "",
    safeguards: "", recommendations: "",
  });

  const [detail, setDetail] = useState(null);
  const [mitigation, setMitigation] = useState({ title: "", description: "", effectiveness: 50, control_type: "", owner: "", due_date: "" });

  const load = async () => {
    setLoading(true);
    try {
      const [d, q] = await Promise.all([
        api.get("/dpia-workspace/dashboard"),
        api.get("/dpia-workspace/questionnaire"),
      ]);
      setDash(d.data);
      setQuestions(q.data.items || []);
    } catch { toast.error("Failed to load"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openScreen = () => {
    setScreenAns({}); setScreenName(""); setScreenResult(null); setScreenModal(true);
  };

  const doScreen = async () => {
    try {
      const { data } = await api.post("/dpia-workspace/screen", {
        activity_name: screenName || "Untitled activity",
        answers: screenAns,
      });
      setScreenResult(data);
    } catch { toast.error("Failed"); }
  };

  const promoteToCase = () => {
    setCaseForm({
      ...caseForm,
      activity_name: screenName,
      answers: screenAns,
    });
    setScreenModal(false);
    setCaseModal(true);
  };

  const createCase = async () => {
    try {
      await api.post("/dpia-workspace/cases", caseForm);
      toast.success("DPIA case created");
      setCaseModal(false);
      setCaseForm({ activity_name: "", scale_of_processing: "medium", answers: {}, risks_to_principals: "", necessity_assessment: "", proportionality_assessment: "", safeguards: "", recommendations: "" });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openDetail = (c) => { setDetail(c); setMitigation({ title: "", description: "", effectiveness: 50, control_type: "", owner: "", due_date: "" }); };

  const addMitigation = async () => {
    try {
      const { data } = await api.post(`/dpia-workspace/cases/${detail.id}/mitigation`, mitigation);
      toast.success(`Residual risk: ${data.residual_risk}`);
      setMitigation({ title: "", description: "", effectiveness: 50, control_type: "", owner: "", due_date: "" });
      // reload detail
      const { data: allData } = await api.get("/dpia-workspace/dashboard");
      setDash(allData);
      const fresh = (allData.cases || []).find(x => x.id === detail.id);
      if (fresh) setDetail(fresh);
    } catch { toast.error("Failed"); }
  };

  const rmMitigation = async (mid) => {
    try {
      await api.delete(`/dpia-workspace/cases/${detail.id}/mitigation/${mid}`);
      const { data: allData } = await api.get("/dpia-workspace/dashboard");
      setDash(allData);
      const fresh = (allData.cases || []).find(x => x.id === detail.id);
      if (fresh) setDetail(fresh);
    } catch { toast.error("Failed"); }
  };

  const doSignoff = async (decision) => {
    const comment = window.prompt(`${decision === "approve" ? "Approval" : "Rejection"} comment:`, "") || "";
    try {
      await api.post(`/dpia-workspace/cases/${detail.id}/sign-off`, { decision, comment });
      toast.success(`Signed off: ${decision}`);
      const { data: allData } = await api.get("/dpia-workspace/dashboard");
      setDash(allData);
      const fresh = (allData.cases || []).find(x => x.id === detail.id);
      if (fresh) setDetail(fresh);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const genNarrative = async () => {
    toast.info("Generating narrative...");
    try {
      const { data } = await api.post(`/dpia-workspace/cases/${detail.id}/generate-narrative`);
      setDetail({ ...detail, report_content: data.report_content });
      toast.success("Narrative generated");
    } catch { toast.error("Failed"); }
  };

  const exportPdf = async (caseId) => {
    try {
      const res = await api.get(`/dpia-workspace/cases/${caseId}/export.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `dpia_${caseId.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch { toast.error("PDF export failed"); }
  };

  if (loading && !dash) return <Loader />;

  return (
    <div>
      <PageHeader
        title="DPIA Workspace"
        kicker="DPDP Sec. 10 Data Protection Impact Assessment"
        description="Structured questionnaire, risk scoring, mitigation planning, and multi-role sign-off for high-risk processing."
        testId="dpia-page-header"
        actions={
          <>
            <Btn variant="secondary" onClick={load} data-testid="dpia-refresh"><Icons.RefreshCw className="w-3.5 h-3.5" /> Refresh</Btn>
            <Btn variant="secondary" onClick={openScreen} data-testid="dpia-screen"><Icons.Search className="w-3.5 h-3.5" /> Screen activity</Btn>
            <Btn onClick={() => setCaseModal(true)} data-testid="dpia-new"><Icons.Plus className="w-3.5 h-3.5" /> New DPIA</Btn>
          </>
        }
      />

      <div className="mb-5 flex items-center gap-1 surface rounded-lg p-1 w-fit" style={{ border: "1px solid var(--border)" }}>
        {[
          { key: "dashboard", label: "Dashboard", icon: "LayoutDashboard" },
          { key: "cases", label: "All cases", icon: "FileCheck" },
          { key: "questionnaire", label: "Rubric", icon: "ListChecks" },
        ].map(t => {
          const Icon = Icons[t.icon] || Icons.Circle;
          return (
            <button key={t.key} data-testid={`dpia-tab-${t.key}`} onClick={() => setTab(t.key)}
              className="px-3 py-1.5 text-xs font-medium rounded-md flex items-center gap-1.5"
              style={{ background: tab === t.key ? "var(--primary-soft)" : "transparent", color: tab === t.key ? "var(--primary)" : "var(--muted)" }}>
              <Icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "dashboard" && dash && (
        <div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <StatCard label="Total DPIAs" value={dash.total_cases} testId="stat-total" />
            <StatCard label="Under review" value={dash.by_status?.under_review || 0} tone="warn" testId="stat-review" />
            <StatCard label="Approved" value={dash.by_status?.approved || 0} tone="success" testId="stat-approved" />
            <StatCard label="Critical residual" value={dash.by_risk?.critical || 0} tone="danger" testId="stat-critical" />
          </div>
          <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
            <div className="text-[13px] font-semibold ink mb-3">Pending your review</div>
            {(dash.pending_review || []).length === 0 ? (
              <div className="text-[12px] muted">No pending items.</div>
            ) : (
              <div className="space-y-2" data-testid="pending-list">
                {dash.pending_review.map(c => (
                  <div key={c.id} className="p-3 rounded-md flex items-center justify-between" style={{ border: "1px solid var(--border)" }}>
                    <div>
                      <div className="text-[13px] font-medium ink">{c.activity_name}</div>
                      <div className="text-[10.5px] muted mt-1">
                        Inherent: {c.inherent_risk_rating} · Residual: {c.residual_risk} · {c.mitigations?.length || 0} mitigations
                      </div>
                    </div>
                    <Btn variant="secondary" size="sm" onClick={() => openDetail(c)} data-testid={`dpia-open-${c.id}`}>Open</Btn>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "cases" && (
        (dash?.cases || []).length === 0 ? (
          <EmptyState title="No DPIA cases yet" hint="Start with 'Screen activity' or create a new DPIA case." />
        ) : (
          <div className="surface rounded-xl overflow-hidden" style={{ border: "1px solid var(--border)" }}>
            <table className="w-full text-sm">
              <thead style={{ background: "var(--bg-2)" }}>
                <tr>
                  {["Activity", "Scale", "Inherent", "Residual", "Mitigations", "Status", "Actions"].map(h => (
                    <th key={h} className="text-left px-4 py-2 text-[11px] font-semibold uppercase tracking-wider muted">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody data-testid="cases-tbody">
                {dash.cases.map(c => (
                  <tr key={c.id} style={{ borderTop: "1px solid var(--border)" }} data-testid={`case-${c.id}`}>
                    <td className="px-4 py-2 ink text-[12.5px] font-medium">{c.activity_name}</td>
                    <td className="px-4 py-2 muted text-[11.5px]">{c.scale_of_processing}</td>
                    <td className="px-4 py-2"><Pill label={c.inherent_risk_rating} mapObj={RISK_COLORS} /><div className="text-[10.5px] muted mt-0.5">{c.inherent_risk_score}</div></td>
                    <td className="px-4 py-2"><Pill label={c.residual_risk} mapObj={RISK_COLORS} /><div className="text-[10.5px] muted mt-0.5">{c.residual_risk_score}</div></td>
                    <td className="px-4 py-2 muted text-[12px]">{c.mitigations?.length || 0}</td>
                    <td className="px-4 py-2"><Pill label={c.status} mapObj={STATUS_COLORS} /></td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      <button onClick={() => exportPdf(c.id)} className="text-[11px] font-medium hover:underline mr-2" style={{ color: "var(--primary)" }} data-testid={`case-export-${c.id}`}>Export PDF</button>
                      <button onClick={() => openDetail(c)} className="text-[11px] font-medium hover:underline" style={{ color: "var(--primary)" }} data-testid={`case-open-${c.id}`}>Open</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {tab === "questionnaire" && (
        <div className="grid gap-3" data-testid="rubric-list">
          {questions.map(q => (
            <div key={q.key} className="surface rounded-xl p-4 flex items-start gap-3" style={{ border: "1px solid var(--border)" }}>
              <div className="w-10 h-10 rounded-md flex items-center justify-center shrink-0 text-[13px] font-bold" style={{ background: "var(--primary-soft)", color: "var(--primary)" }}>
                +{q.weight}
              </div>
              <div className="flex-1">
                <div className="text-[13px] font-medium ink">{q.question}</div>
                <div className="text-[10.5px] mono muted mt-1">Trigger: {q.key} · DPDP {q.dpdp}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Screen modal */}
      <Dialog open={screenModal} onOpenChange={setScreenModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>DPIA Screening</DialogTitle>
            <DialogDescription>Answer the risk-trigger questions to determine if a DPIA is required.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[62vh] overflow-y-auto">
            <Field label="Activity name" className="mb-3"><TextInput data-testid="screen-name" value={screenName} onChange={(e) => setScreenName(e.target.value)} placeholder="e.g. Customer facial recognition at store entry" /></Field>
            <div className="space-y-2">
              {questions.map(q => (
                <label key={q.key} className="flex items-start gap-3 p-2 rounded-md cursor-pointer" style={{ border: "1px solid var(--border)" }}>
                  <input type="checkbox" data-testid={`screen-${q.key}`} checked={!!screenAns[q.key]} onChange={(e) => setScreenAns({ ...screenAns, [q.key]: e.target.checked })} />
                  <div className="flex-1">
                    <div className="text-[12.5px] ink">{q.question}</div>
                    <div className="text-[10px] mono muted mt-0.5">+{q.weight} · {q.dpdp}</div>
                  </div>
                </label>
              ))}
            </div>
            {screenResult && (
              <div className="mt-4 p-4 rounded-md" style={{
                background: screenResult.dpia_required ? "var(--warn-soft)" : "var(--success-soft)",
                border: `1px solid ${screenResult.dpia_required ? "var(--warn)" : "var(--success)"}`,
              }} data-testid="screen-result">
                <div className="flex items-center gap-2 mb-2">
                  <Icons.AlertTriangle className="w-4 h-4" style={{ color: screenResult.dpia_required ? "var(--warn)" : "var(--success)" }} />
                  <div className="font-semibold text-[13px] ink">
                    Inherent risk: {screenResult.rating.toUpperCase()} (score {screenResult.score}/100)
                  </div>
                </div>
                <div className="text-[12px] ink">{screenResult.recommendation}</div>
                {(screenResult.triggered_factors || []).length > 0 && (
                  <div className="mt-2 text-[10.5px] muted mono">Triggered: {screenResult.triggered_factors.join(", ")}</div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Btn variant="secondary" onClick={() => setScreenModal(false)}>Close</Btn>
            <Btn variant="secondary" onClick={doScreen} data-testid="screen-run"><Icons.PlayCircle className="w-3.5 h-3.5" /> Score</Btn>
            {screenResult?.dpia_required && (
              <Btn onClick={promoteToCase} data-testid="screen-promote"><Icons.ArrowRight className="w-3.5 h-3.5" /> Create DPIA</Btn>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New case modal */}
      <Dialog open={caseModal} onOpenChange={setCaseModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>New DPIA case</DialogTitle>
            <DialogDescription>Populate the DPDP Sec. 10 assessment. Risk score is computed from the questionnaire.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 max-h-[62vh] overflow-y-auto">
            <Field label="Activity name" required className="col-span-2"><TextInput data-testid="case-name" value={caseForm.activity_name} onChange={(e) => setCaseForm({ ...caseForm, activity_name: e.target.value })} /></Field>
            <Field label="Scale of processing">
              <SelectInput data-testid="case-scale" value={caseForm.scale_of_processing} onChange={(e) => setCaseForm({ ...caseForm, scale_of_processing: e.target.value })}>
                {["small", "medium", "large", "very_large"].map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
              </SelectInput>
            </Field>
            <Field label="Risks to Data Principals" className="col-span-2"><TextArea data-testid="case-risks" value={caseForm.risks_to_principals} onChange={(e) => setCaseForm({ ...caseForm, risks_to_principals: e.target.value })} /></Field>
            <Field label="Necessity assessment" className="col-span-2"><TextArea data-testid="case-necessity" value={caseForm.necessity_assessment} onChange={(e) => setCaseForm({ ...caseForm, necessity_assessment: e.target.value })} /></Field>
            <Field label="Proportionality assessment" className="col-span-2"><TextArea data-testid="case-proportionality" value={caseForm.proportionality_assessment} onChange={(e) => setCaseForm({ ...caseForm, proportionality_assessment: e.target.value })} /></Field>
            <Field label="Safeguards planned" className="col-span-2"><TextArea data-testid="case-safeguards" value={caseForm.safeguards} onChange={(e) => setCaseForm({ ...caseForm, safeguards: e.target.value })} /></Field>
            {questions.length > 0 && (
              <div className="col-span-2">
                <div className="text-[11px] font-semibold uppercase tracking-wider muted mb-2">Trigger factors</div>
                <div className="grid grid-cols-2 gap-1">
                  {questions.map(q => (
                    <label key={q.key} className="flex items-center gap-2 p-1.5 text-[11.5px]">
                      <input type="checkbox" checked={!!caseForm.answers[q.key]} onChange={(e) => setCaseForm({ ...caseForm, answers: { ...caseForm.answers, [q.key]: e.target.checked } })} />
                      <span>{q.question}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Btn variant="secondary" onClick={() => setCaseModal(false)}>Cancel</Btn>
            <Btn onClick={createCase} disabled={!caseForm.activity_name} data-testid="case-create">Create DPIA</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail modal */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-4xl">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {detail.activity_name}
                  <Pill label={detail.status} mapObj={STATUS_COLORS} />
                </DialogTitle>
                <DialogDescription>
                  Inherent: <Pill label={detail.inherent_risk_rating} mapObj={RISK_COLORS} /> · Residual: <Pill label={detail.residual_risk} mapObj={RISK_COLORS} /> · Score {detail.residual_risk_score}/100
                </DialogDescription>
              </DialogHeader>
              <div className="max-h-[68vh] overflow-y-auto space-y-4">
                {/* Triggered */}
                {(detail.triggered_factors || []).length > 0 && (
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wider muted mb-1">Triggered factors</div>
                    <div className="flex flex-wrap gap-1" data-testid="triggered-factors">
                      {detail.triggered_factors.map(f => (
                        <span key={f} className="px-2 py-[3px] text-[10.5px] font-medium rounded-md mono" style={{ background: "var(--warn-soft)", color: "var(--warn)" }}>{f}</span>
                      ))}
                    </div>
                  </div>
                )}
                {/* Narrative sections */}
                {["risks_to_principals", "necessity_assessment", "proportionality_assessment", "safeguards", "recommendations"].map(k => detail[k] && (
                  <div key={k}>
                    <div className="text-[11px] font-semibold uppercase tracking-wider muted mb-1">{k.replace(/_/g, " ")}</div>
                    <div className="text-[12.5px] ink whitespace-pre-wrap p-3 rounded-md" style={{ background: "var(--bg-2)" }}>{detail[k]}</div>
                  </div>
                ))}
                {/* AI narrative */}
                {detail.report_content && (
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wider muted mb-1">AI-generated report</div>
                    <pre className="mono text-[11.5px] leading-relaxed p-3 rounded-md whitespace-pre-wrap" style={{ background: "var(--bg-2)", color: "var(--ink)" }} data-testid="dpia-narrative">{detail.report_content}</pre>
                  </div>
                )}
                {/* Mitigations */}
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wider muted mb-2">Mitigations ({detail.mitigations?.length || 0})</div>
                  <div className="space-y-2" data-testid="mitigations-list">
                    {(detail.mitigations || []).map(m => (
                      <div key={m.id} className="p-3 rounded-md flex items-start gap-3" style={{ border: "1px solid var(--border)" }}>
                        <div className="w-10 h-10 rounded-md flex items-center justify-center shrink-0 text-[11px] font-bold"
                          style={{ background: "var(--primary-soft)", color: "var(--primary)" }}>
                          {m.effectiveness}%
                        </div>
                        <div className="flex-1">
                          <div className="text-[12.5px] font-medium ink">{m.title}</div>
                          <div className="text-[11px] muted mt-0.5">{m.description}</div>
                          {m.owner && <div className="text-[10.5px] muted mt-0.5">Owner: {m.owner} · Due: {m.due_date || "—"}</div>}
                        </div>
                        <button onClick={() => rmMitigation(m.id)} className="text-[11px] hover:underline" style={{ color: "var(--danger)" }}>Remove</button>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2 p-3 rounded-md" style={{ border: "1px solid var(--border)" }}>
                    <Field label="Mitigation title"><TextInput data-testid="mit-title" value={mitigation.title} onChange={(e) => setMitigation({ ...mitigation, title: e.target.value })} /></Field>
                    <Field label="Effectiveness (%)"><TextInput type="number" min={0} max={100} data-testid="mit-eff" value={mitigation.effectiveness} onChange={(e) => setMitigation({ ...mitigation, effectiveness: Number(e.target.value) })} /></Field>
                    <Field label="Description" className="col-span-2"><TextArea data-testid="mit-desc" value={mitigation.description} onChange={(e) => setMitigation({ ...mitigation, description: e.target.value })} /></Field>
                    <Field label="Owner"><TextInput data-testid="mit-owner" value={mitigation.owner} onChange={(e) => setMitigation({ ...mitigation, owner: e.target.value })} /></Field>
                    <Field label="Due (YYYY-MM-DD)"><TextInput data-testid="mit-due" value={mitigation.due_date} onChange={(e) => setMitigation({ ...mitigation, due_date: e.target.value })} /></Field>
                    <div className="col-span-2 flex justify-end">
                      <Btn size="sm" onClick={addMitigation} disabled={!mitigation.title} data-testid="mit-add"><Icons.Plus className="w-3.5 h-3.5" /> Add mitigation</Btn>
                    </div>
                  </div>
                </div>
                {/* Sign-off tracker */}
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wider muted mb-2">Sign-off</div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="signoff-tracker">
                    {["dpo", "legal", "security_officer", "org_admin"].map(role => {
                      const s = detail.sign_off?.[role];
                      return (
                        <div key={role} className="p-2 rounded-md" style={{ border: "1px solid var(--border)" }}>
                          <div className="text-[11px] font-semibold ink">{role.replace(/_/g, " ")}</div>
                          {s ? (
                            <>
                              <Pill label={s.decision} mapObj={s.decision === "approve" ? { approve: RISK_COLORS.low } : { reject: RISK_COLORS.high }} />
                              <div className="text-[10px] muted mt-1 truncate">{s.signed_by}</div>
                              <div className="text-[10px] muted">{s.signed_at?.slice(0, 10)}</div>
                            </>
                          ) : <div className="text-[10.5px] muted mt-1">Pending</div>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Btn variant="secondary" onClick={() => exportPdf(detail.id)} data-testid="dpia-export-pdf"><Icons.Download className="w-3.5 h-3.5" /> Export PDF</Btn>
                <Btn variant="secondary" onClick={genNarrative} data-testid="dpia-gen"><Icons.Sparkles className="w-3.5 h-3.5" /> Generate AI narrative</Btn>
                {canSignoff && (
                  <>
                    <Btn variant="secondary" onClick={() => doSignoff("reject")} data-testid="dpia-reject" style={{ color: "var(--danger)" }}>Reject</Btn>
                    <Btn onClick={() => doSignoff("approve")} data-testid="dpia-approve">Approve & sign-off</Btn>
                  </>
                )}
                <Btn variant="secondary" onClick={() => setDetail(null)}>Close</Btn>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
